import type { IUserMinimal } from "@/api/users";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/auth/useAuth";
import { useAuiState } from "@assistant-ui/react";

function initialsOf(user: IUserMinimal): string {
    const first = user.first_name?.[0] ?? "";
    const last = user.last_name?.[0] ?? "";
    return (first + last).toUpperCase() || "?";
}

function isUserMinimal(value: unknown): value is IUserMinimal {
    if (typeof value !== "object" || value === null) return false;
    const candidate = value as { id?: unknown };
    return typeof candidate.id === "number";
}

/**
 * Rendered only inside `UserMessage`. Sender sentinels:
 *   - `undefined` indicates a live composer row (assistant-ui's useLocalRuntime
 *     omits `metadata.custom.sender`). Fall back to the viewer; inside a
 *     UserMessage with no stored sender, the viewer IS the author.
 *   - `null` indicates legacy rows pre-dating the sender column. Render
 *     nothing; attributing them to the viewer would mis-label historical
 *     authors.
 *   - any non-conforming object — e.g. a future runtime stuffing a string
 *     into `metadata.custom.sender` — is treated as legacy/unknown to keep
 *     the chip from rendering a partially-constructed user.
 */
export const MessageSenderChip = () => {
    const { user } = useAuth();

    // `metadata.custom.sender` is consumer-owned and weakly typed by
    // assistant-ui — guard each hop instead of casting through, so a
    // missing `metadata` or `custom` slot can't throw on read.
    const sender = useAuiState((s) => {
        const raw = s.message?.metadata?.custom?.sender;
        if (raw === null) return null;
        if (raw === undefined) return undefined;
        return isUserMinimal(raw) ? raw : null;
    });

    if (sender === null) return null;
    if (!user) return null;

    const effectiveSender: IUserMinimal = sender ?? user;
    const isCurrentUserSender = effectiveSender.id === user.id;

    const senderInitials = isCurrentUserSender
        ? "Y"
        : initialsOf(effectiveSender);
    const senderFirstName = isCurrentUserSender
        ? "You"
        : effectiveSender.first_name;

    return (
        <div className="mb-1 flex items-center gap-1.5 justify-self-end">
            <Avatar className="size-5">
                <AvatarFallback className="text-[8px] font-medium">
                    {senderInitials}
                </AvatarFallback>
            </Avatar>
            <span className="text-muted-foreground max-w-[10ch] truncate text-xs leading-tight">
                {senderFirstName}
            </span>
        </div>
    );
};
